import { useCallback, useEffect } from 'react';
import { useObservable } from 'react-use-observable';

import { Button } from 'antd';

import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { FilterActions } from 'v2/components/Shared/FilterActions';
import { FilterPanel } from 'v2/components/Shared/FilterPanel';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import leadsService from 'v2/services/leads/list';
import tableService from 'v2/services/table';

import { Icon } from '@eduzz/ui-antd-theme';

import initialValues, { getInitialValues } from './initialValues';

const leadTypes = [
  {
    label: 'Todos',
    value: ''
  },
  {
    label: 'Abandono de carrinho',
    value: true
  },
  {
    label: 'Clicou em comprar',
    value: false
  }
];

const Filter = () => {
  useEffect(() => {
    leadsService.loadAffiliates();
    leadsService.loadCampaigns();
  }, []);

  const [isLoading] = useObservable(() => tableService.isLoading(), []);

  const [affiliates] = useObservable(() => leadsService.getAffiliates(), []);

  const [campaigns] = useObservable(() => leadsService.getCampaigns(), []);

  const onSubmit = useCallback((values: typeof initialValues, { setSubmitting }: FormikProps<typeof initialValues>) => {
    leadsService.setFilter({ ...values });
    setSubmitting(false);
  }, []);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ isValid, isSubmitting, dirty, resetForm }: FormikProps<typeof initialValues>) => {
        const handleClearFilter = () => {
          const clearedValues = getInitialValues();
          resetForm({ values: clearedValues });
          leadsService.cleanFilter();
        };

        return (
          <Form id='leads-filter' data-testid='leads-filter'>
            <FilterPanel id='leads-filter-panel'>
              <div className='grid grid-cols-1 items-end gap-4 sm:grid-cols-2 md:grid-cols-3'>
                <div>
                  <Field label='Período' id='date' name='date' component={DateRangePickerFormField} />
                </div>
                <div>
                  <Field
                    id='product_id'
                    name='product_id'
                    fullWidth
                    label='Produtos'
                    endpoint='/reports/leads/products'
                    component={SelectProduct}
                  />
                </div>
                <div>
                  <Field
                    id='affiliate_id'
                    name='affiliate_id'
                    multiple
                    options={affiliates}
                    fullWidth
                    label='Afiliados'
                    component={SelectFormField}
                  />
                </div>
                <div>
                  <Field
                    id='campaign_id'
                    name='campaign_id'
                    multiple
                    options={campaigns}
                    fullWidth
                    label='Campanhas'
                    component={SelectFormField}
                  />
                </div>
                <div>
                  <Field
                    id='abandonment'
                    name='abandonment'
                    options={leadTypes}
                    fullWidth
                    label='Tipo de lead'
                    component={SelectFormField}
                  />
                </div>
                <div className='md:col-start-3'>
                  <FilterActions>
                    <Button
                      id='clear-button'
                      disabled={!dirty || isSubmitting || isLoading}
                      type='text'
                      onClick={handleClearFilter}
                      loading={isLoading || isSubmitting}
                    >
                      <span id='clear-button-label'>Limpar</span>
                    </Button>

                    <Button
                      id='basic-filter-button'
                      type='primary'
                      disabled={!isValid || isSubmitting || isLoading}
                      htmlType='submit'
                      loading={isLoading || isSubmitting}
                      icon={<Icon.Funnel id='basic-filter-button-icon' />}
                    >
                      <span id='basic-filter-button-label'>Filtrar</span>
                    </Button>
                  </FilterActions>
                </div>
              </div>
            </FilterPanel>
          </Form>
        );
      }}
    </Formik>
  );
};
export default Filter;
